import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const FavoritesContext = createContext();

const STORAGE_KEY = 'theCycle-favorites';

const loadFavorites = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return { players: [], teams: [] };
};

const saveFavorites = (favorites) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
  } catch {}
};

export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState(loadFavorites);

  useEffect(() => {
    saveFavorites(favorites);
  }, [favorites]);

  const toggleFavoritePlayer = useCallback((player) => {
    setFavorites(prev => {
      const id = player.id || player.playerId || `${player.team}-${player.name}`;
      const exists = prev.players.some(p => (p.id || p.playerId || `${p.team}-${p.name}`) === id);
      if (exists) {
        return { ...prev, players: prev.players.filter(p => (p.id || p.playerId || `${p.team}-${p.name}`) !== id) };
      }
      // Store minimal player info
      return {
        ...prev,
        players: [...prev.players, {
          id,
          name: player.name || player.playerName,
          team: player.team || player.teamCode,
          slug: player.slug,
          addedAt: Date.now(),
        }].slice(0, 25), // max 25 favorites
      };
    });
  }, []);

  const toggleFavoriteTeam = useCallback((teamCode) => {
    setFavorites(prev => {
      const exists = prev.teams.includes(teamCode);
      if (exists) {
        return { ...prev, teams: prev.teams.filter(t => t !== teamCode) };
      }
      return { ...prev, teams: [...prev.teams, teamCode].slice(0, 10) };
    });
  }, []);

  const isPlayerFavorite = useCallback((player) => {
    const id = player.id || player.playerId || `${player.team}-${player.name}`;
    return favorites.players.some(p => (p.id || p.playerId || `${p.team}-${p.name}`) === id);
  }, [favorites.players]);

  const isTeamFavorite = useCallback((teamCode) => {
    return favorites.teams.includes(teamCode);
  }, [favorites.teams]);

  return (
    <FavoritesContext.Provider value={{
      favorites,
      favoritePlayers: favorites.players,
      favoriteTeams: favorites.teams,
      toggleFavoritePlayer,
      toggleFavoriteTeam,
      isPlayerFavorite,
      isTeamFavorite,
    }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error('useFavorites must be used within FavoritesProvider');
  return ctx;
};

export default FavoritesContext;
